/**
 * Format a date string as relative time (e.g., "5m ago", "2h ago", "yesterday").
 * Used by TripCard and SummaryCard for "last checked" display.
 *
 * @param dateString - ISO date string, or null/undefined
 * @param fallback - What to return when dateString is absent (default: "pending")
 */
export function formatTimeAgo(dateString: string | null | undefined, fallback = 'pending'): string {
  if (!dateString) return fallback;
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}

/**
 * Format a date as a human-readable watching duration.
 * Communicates investment ("Watching for 3 weeks") rather than
 * recency ("checked 2h ago"), per the Perch Paradox — trust
 * compounds with time, mechanism details stay hidden.
 *
 * @param dateString - ISO date string of when watching started (created_at)
 * @param fallback - What to return when dateString is absent
 */
export function formatWatchingDuration(dateString: string | null | undefined, fallback = 'Just added'): string {
  if (!dateString) return fallback;
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);
  const diffMonths = Math.floor(diffDays / 30);

  if (diffHours < 1) return 'Just added';
  if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'}`;
  if (diffDays === 1) return '1 day';
  if (diffDays < 7) return `${diffDays} days`;
  if (diffWeeks === 1) return '1 week';
  if (diffWeeks < 5) return `${diffWeeks} weeks`;
  if (diffMonths === 1) return '1 month';
  return `${diffMonths} months`;
}
